import React, { createContext, useState, useMemo, ReactNode } from "react"
import { CacheProvider } from "@emotion/react"
import createCache from "@emotion/cache"
import rtlPlugin from "@mui/stylis-plugin-rtl"
import { ThemeOptions, ThemeProvider, createTheme } from "@mui/material/styles"
import { useTranslation } from "react-i18next"
import lightTheme from "src/theme/light-theme"
import darkTheme from "src/theme/dark-theme"
import { isRtlLang } from "src/i18n/config"

interface ThemeContextType {
  toggleTheme: () => void
  mode: "light" | "dark"
  direction: "rtl" | "ltr"
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

interface ThemeContextProviderProps {
  children: ReactNode
}

const themeModes: { [key: string]: "light" | "dark" } = {
  light: "light",
  dark: "dark"
}

const themes: { [key: string]: ThemeOptions } = {
  light: lightTheme,
  dark: darkTheme
}

const rtlCache = createCache({
  key: "muirtl",
  stylisPlugins: [rtlPlugin]
})

const ltrCache = createCache({
  key: "mui"
})

export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({ children }) => {
  const { i18n } = useTranslation()
  const defaultTheme = themeModes[(localStorage.getItem("theme") as "light" | "dark") ?? "light"] ?? "light"
  const [mode, setMode] = useState<"light" | "dark">(defaultTheme)

  const direction: "rtl" | "ltr" = isRtlLang(i18n.resolvedLanguage ?? i18n.language) ? "rtl" : "ltr"

  const toggleTheme = () => {
    const newMode = mode === "light" ? "dark" : "light"
    localStorage.setItem("theme", newMode)
    setMode(newMode)
  }

  const theme = useMemo(
    () => createTheme(themes[mode] ?? lightTheme, { direction }),
    [mode, direction]
  )

  return (
    <CacheProvider value={direction === "rtl" ? rtlCache : ltrCache}>
      <ThemeContext.Provider value={{ toggleTheme, mode, direction }}>
        <ThemeProvider theme={theme}>{children}</ThemeProvider>
      </ThemeContext.Provider>
    </CacheProvider>
  )
}
